import type { CSSProperties } from "react";

const TAB_BAR_LABEL_LINE_HEIGHT = 1.2;

/** 空间底部 Tab 栏布局。 */
export interface SpaceTabBarLayoutConfig {
  /** 图标上沿与 Tab 内容区上边界之间的距离（px） */
  iconTopOffset: number;
  itemPaddingBottom: number;
  itemPaddingX: number;
  itemGap: number;
  iconSize: number;
  labelFontSize: number;
  showRegionDebugColors: boolean;
  regionDebugContentBg: string;
}

export const SPACE_TAB_BAR_LAYOUT = {
  iconTopOffset: 10,
  itemPaddingBottom: 2,
  itemPaddingX: 4,
  itemGap: 2,
  iconSize: 22,
  labelFontSize: 10,
  showRegionDebugColors: false,
  regionDebugContentBg: "#ffc9e5",
} as const satisfies SpaceTabBarLayoutConfig;

export function deriveTabBarContentHeight(
  layout: SpaceTabBarLayoutConfig = SPACE_TAB_BAR_LAYOUT
): number {
  const labelLine = Math.ceil(layout.labelFontSize * TAB_BAR_LABEL_LINE_HEIGHT);
  return (
    layout.iconTopOffset +
    layout.iconSize +
    layout.itemGap +
    labelLine +
    layout.itemPaddingBottom
  );
}

export function tabBarTotalHeightExpr(
  layout: SpaceTabBarLayoutConfig = SPACE_TAB_BAR_LAYOUT
): string {
  return `${deriveTabBarContentHeight(layout)}px`;
}

/** Tab 栏 + 顶部分割线 + safe-area + iOS bleed，列表滚动区底部应预留的总高度 */
export function tabBarReservedBottomCssExpr(
  layout: SpaceTabBarLayoutConfig = SPACE_TAB_BAR_LAYOUT
): string {
  return `calc(${deriveTabBarContentHeight(layout)}px + 1px + env(safe-area-inset-bottom, 0px) + var(--lf-h5-bleed-bottom, 0px))`;
}

export function spaceTabBarLayoutCssVars(
  layout: SpaceTabBarLayoutConfig = SPACE_TAB_BAR_LAYOUT
): CSSProperties {
  const cardBg = "var(--lf-space-card)";
  const useDebug = layout.showRegionDebugColors;
  return {
    ["--lf-h5-tab-bar-content-height" as string]: `${deriveTabBarContentHeight(layout)}px`,
    ["--lf-h5-tab-bar-height" as string]: tabBarTotalHeightExpr(layout),
    ["--lf-h5-tab-bar-reserved-bottom" as string]: tabBarReservedBottomCssExpr(layout),
    ["--lf-h5-tab-bar-shell-bg" as string]: cardBg,
    ["--lf-h5-tab-bar-content-bg" as string]: useDebug ? layout.regionDebugContentBg : cardBg,
    ["--lf-h5-tab-bar-icon-top-offset" as string]: `${layout.iconTopOffset}px`,
    ["--lf-h5-tab-bar-item-padding-bottom" as string]: `${layout.itemPaddingBottom}px`,
    ["--lf-h5-tab-bar-item-padding-x" as string]: `${layout.itemPaddingX}px`,
    ["--lf-h5-tab-bar-item-gap" as string]: `${layout.itemGap}px`,
    ["--lf-h5-tab-bar-icon-size" as string]: `${layout.iconSize}px`,
    ["--lf-h5-tab-bar-label-font-size" as string]: `${layout.labelFontSize}px`,
  };
}
